import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { Offcanvas } from 'react-bootstrap';
import DrawerWrapper, { DrawerFooter } from '../DrawerWrapper';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  CreateDailyPersonnelEntryFormFields,
  EditDailyPersonnelEntryFormFields,
  IncidentLogEntry,
  SignInInformationRow,
  useCreateIncidentMemberSession,
  useUpdateIncidentMemberSession,
  useGetIncidentLogs,
} from 'hooks/incident/useWorkLog';
import FormDateTimeField from '../formFields/FormDateTimeField';
import FormSelectField from '../formFields/FormSelectField';
import { useActualIncidentId } from '../hooks/commonHooks';
import { toast } from 'react-toastify';

import { useIncidentRoster } from '../hooks/incidentRoster/useIncidentRoster';

type DailyPersonnelDrawerProps = {
  toggle: () => void;
  selectedEntity?: SignInInformationRow;
};

type FormFields =
  | CreateDailyPersonnelEntryFormFields
  | EditDailyPersonnelEntryFormFields;

const DailyPersonnelDrawer: React.FC<DailyPersonnelDrawerProps> = ({
  toggle,
  selectedEntity,
}) => {
  const isEditMode = !!selectedEntity;
  const incidentId = useActualIncidentId();

  // Hooks for create mode
  const roster = useIncidentRoster();
  const { data: incidentLogs = [] } = useGetIncidentLogs();

  // Mutations
  const createSessionMutation = useCreateIncidentMemberSession();
  const updateSessionMutation = useUpdateIncidentMemberSession();

  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: isEditMode
      ? {
          signInTime: new Date(selectedEntity.signInTime),
          signOutTime: selectedEntity.signOutTime
            ? new Date(selectedEntity.signOutTime)
            : null,
        }
      : {
          signInTime: new Date(),
          signOutTime: null,
        },
  });

  const { handleSubmit, control } = form;

  const onSubmit = async (formData: FormFields) => {
    const signInTime = new Date(formData.signInTime);
    const signOutTime = formData.signOutTime
      ? new Date(formData.signOutTime)
      : null;

    if (signOutTime && signInTime > signOutTime) {
      toast.error('Time In must be before Time Out');
      return;
    }

    if (isEditMode) {
      await updateSessionMutation.mutateAsync({
        sessionId: selectedEntity.sessionId,
        workLogId: selectedEntity.workLogId,
        incidentId,
        userGuid: selectedEntity.userGuid,
        signInTime: signInTime.toISOString(),
        signOutTime: signOutTime?.toISOString() || null,
      });
    } else {
      // Create mode - need to validate and find workLogId
      const createFormData = formData as CreateDailyPersonnelEntryFormFields;

      const userLog = incidentLogs.find(
        (log: IncidentLogEntry) => log.userGuid === createFormData.userGuid
      );
      const workLog = userLog?.logs?.[0];

      if (!workLog?.workLogId) {
        toast.error('User must be checked in before creating a session');
        return;
      }

      await createSessionMutation.mutateAsync({
        incidentId,
        userGuid: createFormData.userGuid,
        workLogId: workLog.workLogId,
        signInTime: signInTime.toISOString(),
        signOutTime: signOutTime?.toISOString() || null,
      });
    }

    toggle();
  };

  return (
    <DrawerWrapper
      toggle={toggle}
      title={isEditMode ? 'Edit Sign-In Sheet Row' : 'Create Sign-In Sheet Row'}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="h-100 d-flex flex-column"
        >
          <Offcanvas.Body className="d-flex flex-column gap-2 flex-grow-1">
            {!isEditMode && (
              <Controller
                control={control}
                name="userGuid"
                render={() => (
                  <FormSelectField
                    title="Incident Member"
                    fieldName="userGuid"
                    options={
                      roster.data
                        ? roster.data
                            .filter((u) => u.userName || u.emailAddress)
                            .map((u) => {
                              return {
                                label: u.userName ?? (u.emailAddress as string),
                                value: u.userId,
                              };
                            })
                        : []
                    }
                    variant="half"
                  />
                )}
              />
            )}
            <Controller
              control={control}
              name="signInTime"
              render={() => (
                <FormDateTimeField
                  title="Time In"
                  fieldName="signInTime"
                  variant="half"
                />
              )}
            />
            <Controller
              control={control}
              name="signOutTime"
              render={() => (
                <FormDateTimeField
                  title="Time Out (Optional)"
                  fieldName="signOutTime"
                  variant="half"
                />
              )}
            />
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton className="button--primary" type="submit">
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default DailyPersonnelDrawer;
